"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { archiveConversationAction } from "@/actions/serverActions/ai-agent/archiveConversationAction";
import { createConversationAction } from "@/actions/serverActions/ai-agent/createConversationAction";
import { getConversationMessagesAction } from "@/actions/serverActions/ai-agent/getConversationMessagesAction";
import { getConversationsAction } from "@/actions/serverActions/ai-agent/getConversationsAction";
import { renameConversationAction } from "@/actions/serverActions/ai-agent/renameConversationAction";
import {
  type ChatConversation,
  type ChatMessage,
  isNonFatalWsError,
  type ServerFrame,
} from "@/app/ai-agent/types";
import { useServerAction } from "@/hooks/useServerAction";
import {
  PCM_SAMPLE_RATE,
  type PcmCaptureHandle,
  startPcmCapture,
} from "@/lib/ai-gateway/pcmCapture";
import {
  closeStreamSession,
  openStreamDownlink,
  sendStreamAudio,
  sendStreamFrame,
} from "@/lib/ai-gateway/streamClient";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import {
  addUserMessage,
  appendDelta,
  completeAssistantMessage,
  removeConversationLocal,
  renameConversationLocal,
  setActiveConversation,
  setConversations,
  setConversationsLoading,
  setError,
  setMessages,
  setMessagesLoading,
  setToolActivity,
  setTurnStatus,
  settleStreamingMessages,
  startAssistantMessage,
  startNewConversation,
  togglePinLocal,
  upsertConversation,
} from "@/store/Features/states/ai-agent/aiChatSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

const EMPTY_MESSAGES: ChatMessage[] = [];

let localIdSeq = 0;
function localId(prefix: string): string {
  localIdSeq += 1;
  return `local-${prefix}-${localIdSeq}`;
}

export type TranscriptHandler = (
  text: string,
  kind: "interim" | "final",
) => void;

/**
 * The single entry point the AI chat UI consumes. Owns conversation/message
 * state (via the `aiChat` Redux slice), the session stream lifecycle (through
 * `/api/ai-gateway/stream`), composer dictation, and sidebar rename/archive/pin.
 *
 * The UI never touches the socket, the proxy, or the REST actions directly —
 * it renders `conversations`/`messages`/`turnStatus`/`toolActivity` and calls
 * the returned methods. See docs/ai-gateway-integration-guide.md.
 */
export function useAiChat(opts?: {
  /** Live STT captions → composer input (never auto-sends). */
  onTranscript?: TranscriptHandler;
  /** Called when dictation must stop (error / stream drop). */
  onDictationEnded?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { showError } = useAlert();

  const onTranscriptRef = useRef(opts?.onTranscript);
  const onDictationEndedRef = useRef(opts?.onDictationEnded);
  onTranscriptRef.current = opts?.onTranscript;
  onDictationEndedRef.current = opts?.onDictationEnded;

  const conversations = useAppSelector((s) => s.aiChat.conversations);
  const activeConversationId = useAppSelector(
    (s) => s.aiChat.activeConversationId,
  );
  const messagesByConversation = useAppSelector(
    (s) => s.aiChat.messagesByConversation,
  );
  const loadedConversationIds = useAppSelector(
    (s) => s.aiChat.loadedConversationIds,
  );
  const turnStatus = useAppSelector((s) => s.aiChat.turnStatus);
  const toolActivity = useAppSelector((s) => s.aiChat.toolActivity);
  const error = useAppSelector((s) => s.aiChat.error);
  const conversationsLoading = useAppSelector(
    (s) => s.aiChat.conversationsLoading,
  );
  const messagesLoading = useAppSelector((s) => s.aiChat.messagesLoading);
  const pinnedIds = useAppSelector((s) => s.aiChat.pinnedIds);

  const createConversation = useServerAction(createConversationAction);
  const renameConversation = useServerAction(renameConversationAction);
  const archiveConversation = useServerAction(archiveConversationAction);

  /** AbortController for the long-lived GET downlink. */
  const streamAbortRef = useRef<AbortController | null>(null);
  /** Conversation id the downlink is currently attached to. */
  const streamConversationIdRef = useRef<string | null>(null);
  /** Id of the assistant bubble currently streaming (set on `message_start`). */
  const activeAssistantIdRef = useRef<string | null>(null);
  /** True while a text turn is in flight (gates send; cancel sends `cancel`). */
  const turnActiveRef = useRef(false);
  const pcmCaptureRef = useRef<PcmCaptureHandle | null>(null);
  const dictatingRef = useRef(false);
  const loadConversationsRef = useRef<() => Promise<void>>(async () => {});

  const messages = useMemo(
    () =>
      activeConversationId
        ? (messagesByConversation[activeConversationId] ?? EMPTY_MESSAGES)
        : EMPTY_MESSAGES,
    [activeConversationId, messagesByConversation],
  );

  // ── Sidebar list ──────────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    dispatch(setConversationsLoading(true));
    try {
      dispatch(setConversations(await getConversationsAction()));
    } catch {
      showError("Couldn't load your chats.");
    } finally {
      dispatch(setConversationsLoading(false));
    }
  }, [dispatch, showError]);

  loadConversationsRef.current = loadConversations;

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  const stopPcmCapture = useCallback(() => {
    pcmCaptureRef.current?.stop();
    pcmCaptureRef.current = null;
    dictatingRef.current = false;
  }, []);

  const detachStream = useCallback(async (conversationId: string | null) => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    const id = conversationId ?? streamConversationIdRef.current;
    streamConversationIdRef.current = null;
    if (id) await closeStreamSession(id);
  }, []);

  // ── Frame handling (shared by the long-lived downlink) ────────────────────
  const handleFrame = useCallback(
    (conversationId: string, frame: ServerFrame) => {
      switch (frame.type) {
        case "message_start":
          activeAssistantIdRef.current = frame.message_id;
          dispatch(
            startAssistantMessage({
              conversationId,
              messageId: frame.message_id,
            }),
          );
          break;
        case "text_delta":
          if (activeAssistantIdRef.current) {
            dispatch(
              appendDelta({
                conversationId,
                messageId: activeAssistantIdRef.current,
                text: frame.text,
              }),
            );
          }
          break;
        case "tool_activity":
          dispatch(
            setToolActivity(
              frame.status === "running"
                ? { tool: frame.tool, label: frame.label }
                : null,
            ),
          );
          break;
        case "message_complete":
          dispatch(
            completeAssistantMessage({
              conversationId,
              messageId: frame.message_id,
              content: frame.content,
              incomplete: frame.incomplete,
            }),
          );
          dispatch(setToolActivity(null));
          activeAssistantIdRef.current = null;
          break;
        case "done":
          turnActiveRef.current = false;
          dispatch(settleStreamingMessages(conversationId));
          dispatch(setToolActivity(null));
          dispatch(setTurnStatus("idle"));
          void loadConversationsRef.current();
          break;
        case "cancelled":
          turnActiveRef.current = false;
          dispatch(settleStreamingMessages(conversationId));
          dispatch(setToolActivity(null));
          dispatch(setTurnStatus("idle"));
          break;
        case "error": {
          const duringDictation = dictatingRef.current;
          if (duringDictation || !isNonFatalWsError(frame.error)) {
            stopPcmCapture();
            onDictationEndedRef.current?.();
          }
          if (turnActiveRef.current || !duringDictation) {
            turnActiveRef.current = false;
            dispatch(settleStreamingMessages(conversationId));
            dispatch(setToolActivity(null));
            dispatch(setTurnStatus("idle"));
          }
          dispatch(setError(frame.message));
          showError(frame.message);
          break;
        }
        case "reauth_required":
          turnActiveRef.current = false;
          stopPcmCapture();
          onDictationEndedRef.current?.();
          dispatch(settleStreamingMessages(conversationId));
          dispatch(setToolActivity(null));
          dispatch(setTurnStatus("idle"));
          dispatch(setError(frame.message));
          showError(frame.message);
          break;
        case "transcript_interim":
          onTranscriptRef.current?.(frame.text, "interim");
          break;
        case "transcript_final":
          onTranscriptRef.current?.(frame.text, "final");
          break;
        case "conversation_titled":
          void loadConversationsRef.current();
          break;
      }
    },
    [dispatch, showError, stopPcmCapture],
  );

  const ensureStream = useCallback(
    async (conversationId: string) => {
      if (
        streamConversationIdRef.current === conversationId &&
        streamAbortRef.current &&
        !streamAbortRef.current.signal.aborted
      ) {
        return;
      }

      // Tear down previous conversation's stream.
      if (streamConversationIdRef.current) {
        await detachStream(streamConversationIdRef.current);
      }

      const controller = new AbortController();
      streamAbortRef.current = controller;
      streamConversationIdRef.current = conversationId;

      try {
        const { done } = await openStreamDownlink({
          conversationId,
          signal: controller.signal,
          onFrame: (frame) => handleFrame(conversationId, frame),
        });
        void done.catch((e) => {
          if (e instanceof DOMException && e.name === "AbortError") return;
          if (streamConversationIdRef.current !== conversationId) return;
          const message =
            e instanceof Error
              ? e.message
              : "The assistant connection dropped.";
          dispatch(setError(message));
          showError(message);
          turnActiveRef.current = false;
          stopPcmCapture();
          onDictationEndedRef.current?.();
          dispatch(setTurnStatus("idle"));
          streamConversationIdRef.current = null;
          streamAbortRef.current = null;
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        streamConversationIdRef.current = null;
        streamAbortRef.current = null;
        throw e;
      }
    },
    [detachStream, dispatch, handleFrame, showError, stopPcmCapture],
  );

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (activeConversationId) return activeConversationId;
    const created = await createConversation();
    if (!created.success || !("data" in created) || !created.data) {
      showError(created.message ?? "Couldn't start a new chat.");
      return null;
    }
    const conv = created.data as ChatConversation;
    dispatch(upsertConversation(conv));
    dispatch(setMessages({ conversationId: conv.id, messages: [] }));
    dispatch(setActiveConversation(conv.id));
    return conv.id;
  }, [activeConversationId, createConversation, dispatch, showError]);

  // Keep stream attached while a thread is active.
  useEffect(() => {
    if (!activeConversationId) {
      stopPcmCapture();
      void detachStream(null);
      return;
    }
    void ensureStream(activeConversationId).catch((e) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      const message =
        e instanceof Error ? e.message : "Couldn't connect to the assistant.";
      showError(message);
    });
  }, [
    activeConversationId,
    detachStream,
    ensureStream,
    showError,
    stopPcmCapture,
  ]);

  // ── Thread selection (load history once per thread) ────────────────────────
  const selectConversation = useCallback(
    async (id: string) => {
      stopPcmCapture();
      onDictationEndedRef.current?.();
      dispatch(setActiveConversation(id));
      if (loadedConversationIds.includes(id)) return;
      dispatch(setMessagesLoading(true));
      try {
        dispatch(
          setMessages({
            conversationId: id,
            messages: await getConversationMessagesAction(id),
          }),
        );
      } catch {
        showError("Couldn't load that chat's history.");
      } finally {
        dispatch(setMessagesLoading(false));
      }
    },
    [dispatch, loadedConversationIds, showError, stopPcmCapture],
  );

  const newConversation = useCallback(() => {
    stopPcmCapture();
    onDictationEndedRef.current?.();
    void detachStream(streamConversationIdRef.current);
    dispatch(startNewConversation());
  }, [detachStream, dispatch, stopPcmCapture]);

  const sendMessage = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || turnStatus !== "idle") return;

      const conversationId = await ensureConversation();
      if (!conversationId) return;

      try {
        await ensureStream(conversationId);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Couldn't connect to the assistant.";
        dispatch(setError(message));
        showError(message);
        return;
      }

      dispatch(
        addUserMessage({
          conversationId,
          message: {
            id: localId("user"),
            role: "user",
            content,
            status: "complete",
            createdAt: new Date().toISOString(),
          },
        }),
      );

      dispatch(setError(null));
      dispatch(setTurnStatus("streaming"));
      turnActiveRef.current = true;

      try {
        await sendStreamFrame(conversationId, {
          type: "user_message",
          content,
        });
      } catch (e) {
        turnActiveRef.current = false;
        dispatch(setTurnStatus("idle"));
        dispatch(settleStreamingMessages(conversationId));
        const message =
          e instanceof Error
            ? e.message
            : "The assistant is unavailable right now.";
        dispatch(setError(message));
        showError(message);
      }
    },
    [dispatch, ensureConversation, ensureStream, showError, turnStatus],
  );

  const cancelTurn = useCallback(() => {
    const conversationId = streamConversationIdRef.current;
    if (!conversationId || !turnActiveRef.current) return;
    dispatch(setTurnStatus("cancelling"));
    void sendStreamFrame(conversationId, { type: "cancel" }).catch(() => {
      turnActiveRef.current = false;
      dispatch(setTurnStatus("idle"));
      dispatch(settleStreamingMessages(conversationId));
    });
  }, [dispatch]);

  const startDictation = useCallback(async (): Promise<boolean> => {
    if (dictatingRef.current || turnStatus !== "idle") return false;

    const conversationId = await ensureConversation();
    if (!conversationId) return false;

    try {
      await ensureStream(conversationId);
      await sendStreamFrame(conversationId, {
        type: "audio_start",
        sample_rate: PCM_SAMPLE_RATE,
        encoding: "linear16",
        auto_submit: false,
      });

      const capture = await startPcmCapture((pcm) => {
        void sendStreamAudio(conversationId, pcm).catch(() => {
          /* drop chunk on transient failure; stop on next error frame */
        });
      });
      pcmCaptureRef.current = capture;
      dictatingRef.current = true;
      return true;
    } catch (e) {
      stopPcmCapture();
      const message =
        e instanceof Error && e.name === "NotAllowedError"
          ? "Microphone permission is required for voice input."
          : e instanceof Error
            ? e.message
            : "Couldn't start voice input.";
      showError(message);
      onDictationEndedRef.current?.();
      return false;
    }
  }, [ensureConversation, ensureStream, showError, stopPcmCapture, turnStatus]);

  const stopDictation = useCallback(async () => {
    const conversationId = streamConversationIdRef.current;
    const wasDictating = dictatingRef.current;
    stopPcmCapture();
    if (wasDictating && conversationId) {
      try {
        await sendStreamFrame(conversationId, { type: "audio_end" });
      } catch {
        /* stream may already be gone */
      }
    }
  }, [stopPcmCapture]);

  // ── Sidebar writes (optimistic, with rollback on failure) ──────────────────
  const handleRename = useCallback(
    async (id: string, title: string) => {
      const previous = conversations.find((c) => c.id === id)?.title ?? null;
      dispatch(renameConversationLocal({ id, title }));
      const result = await renameConversation({ id, title });
      if (!result.success) {
        if (previous !== null) {
          dispatch(renameConversationLocal({ id, title: previous }));
        }
        showError(result.message ?? "Couldn't rename the chat.");
      }
    },
    [conversations, dispatch, renameConversation, showError],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const snapshot = conversations.find((c) => c.id === id);
      if (streamConversationIdRef.current === id) {
        stopPcmCapture();
        onDictationEndedRef.current?.();
        await detachStream(id);
      }
      dispatch(removeConversationLocal(id));
      const result = await archiveConversation({ id });
      if (!result.success) {
        if (snapshot) dispatch(upsertConversation(snapshot));
        showError(result.message ?? "Couldn't delete the chat.");
      }
    },
    [
      archiveConversation,
      conversations,
      detachStream,
      dispatch,
      showError,
      stopPcmCapture,
    ],
  );

  const togglePin = useCallback(
    (id: string) => {
      dispatch(togglePinLocal(id));
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      stopPcmCapture();
      const id = streamConversationIdRef.current;
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      streamConversationIdRef.current = null;
      if (id) void closeStreamSession(id);
    };
  }, [stopPcmCapture]);

  return {
    // state
    conversations,
    activeConversationId,
    messages,
    turnStatus,
    toolActivity,
    error,
    conversationsLoading,
    messagesLoading,
    pinnedIds,
    // actions
    loadConversations,
    selectConversation,
    newConversation,
    ensureConversation,
    sendMessage,
    cancelTurn,
    startDictation,
    stopDictation,
    renameConversation: handleRename,
    deleteConversation: handleDelete,
    togglePin,
  };
}
